import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, RefreshCw, Sparkles } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/Card";
import { SettingsBanner } from "../../components/ui/SettingsBanner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/Table";
import { settingsApi } from "../../utils/api";

const DEFAULT_RANGE_DAYS = 14;

// The Users table is the whole account list now, so it pages. Small on purpose: activated
// users sort first, so page one is the one an admin actually works with.
const USERS_PAGE_SIZE = 25;

// Each knob, with the unit and bounds the API enforces. `hint` is the part a reader
// cannot get from the name: what turning it actually costs.
const NUMBER_SETTINGS = [
  {
    key: "daily_budget_usd",
    label: "Daily budget (USD)",
    step: 0.5,
    min: 0,
    hint: "Past this, every request is served by the ranker for the rest of the UTC day.",
  },
  {
    key: "shortlist_size",
    label: "Shortlist size",
    step: 5,
    min: 1,
    max: 200,
    hint: "Candidates shown to the model. The main cost lever — cost moves with it linearly.",
  },
  {
    key: "pick_size",
    label: "Picks",
    step: 1,
    min: 1,
    max: 100,
    hint: "How many events the model picks and explains.",
  },
  {
    key: "cache_ttl_hours",
    label: "Cache TTL (hours)",
    step: 1,
    min: 1,
    max: 720,
    hint: "A backstop only: the key already covers the shortlist, profile and filters.",
  },
  {
    key: "timeout_seconds",
    label: "Feed timeout (seconds)",
    step: 0.5,
    min: 0.5,
    max: 30,
    hint: "This call sits in page-load time. On timeout the feed falls back to the ranker.",
  },
  {
    key: "explain_timeout_seconds",
    label: "On demand explanation (seconds)",
    step: 0.5,
    min: 0,
    max: 30,
    // Its own knob because the two calls are not the same size — the feed's is sized for
    // a shortlist of 60 returning 24 picks, this one buys one sentence (~3s measured).
    hint: "One \u201cWhy go?\u201d press, on request. 0 works it out from the reasoning effort \u2014 leave it there unless you have measured this call. Too low and every press is billed and then cut off.",
  },
];

// Every reason a request was not curated. Spelled out because "budget_exhausted" and
// "provider_error" mean very different things: one is the rail working, the other is
// money spent for nothing.
// What each position of the master switch does, in the order it escalates.
const MODES = [
  { value: "OFF", label: "Off", hint: "Nobody is curated, whatever the list below says." },
  {
    value: "SELECTED",
    label: "Selected users",
    hint: "Only the users activated below. Your list is untouched by the other two.",
  },
  {
    value: "EVERYONE",
    label: "Everyone",
    hint: "Every signed-in, onboarded user — overrides the list below without editing it.",
  },
];

// The second axis: `MODES` says who is curated, this says what they get. Separate
// because the questions are independent — "selected users, on demand" is a real
// configuration, and a single list could only spell it by multiplying out.
const STRATEGIES = [
  {
    value: "FEED",
    label: "Whole feed, up front",
    hint: "One call per feed re-ranks a shortlist and explains every pick — paying for all 24 whether or not anyone reads one.",
  },
  {
    value: "ON_DEMAND",
    label: "One at a time, on request",
    hint: "The ranked order is left alone; a sentence is bought only when a reader presses \u201cWhy?\u201d. Far cheaper at real click rates, but nothing is re-ranked.",
  },
];

// The stored outcome vocabulary, which is not the settings one: these say what ran.
const MODE_LABELS = {
  CURATOR: "Whole feed",
  ON_DEMAND: "On request",
  SIGNALS: "Ranker only",
};

const FALLBACK_LABELS = {
  not_eligible: "Not onboarded",
  not_selected: "Not in the selected list",
  user_budget_exhausted: "Personal budget reached",
  budget_exhausted: "Budget reached",
  spend_unknown: "Ledger unreadable",
  model_unpriced: "Model has no price",
  no_client: "No API key",
  no_shortlist: "Nothing to discover",
  // Reachable, and was missing: `_output_budget` cut a working reply off mid-JSON, so it
  // was billed in full and the fix is a higher ceiling, not a prompt rewrite.
  output_truncated: "Reply cut short (paid)",
  // Deliberately absent: `on_demand` and `feed_strategy`. Both are unbilled refusals, and
  // an unbilled refusal records no `llm_usage` — the query behind this panel keys on
  // `llm_usage->>'fallback'`, so they can never appear. Several neighbours above
  // (not_eligible, not_selected, budget_exhausted) are dead for the same reason; that
  // predates #544 and is not fixed here, but adding two more labels that cannot render
  // would have made the list look more complete than the panel is.
  provider_error: "Provider error (paid)",
  no_picks: "No picks returned (paid)",
  // Not "(paid)" like its two neighbours: the call never reached OpenAI, so nothing was
  // billed — and unlike them it is ours to fix rather than something to wait out.
  client_error: "Failed before the call",
};

const isoDaysAgo = (days) => {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
};

const money = (value, digits = 2) =>
  typeof value === "number" ? `$${value.toFixed(digits)}` : "—";

const percent = (value) => (typeof value === "number" ? `${Math.round(value * 100)}%` : "—");

const CuratorPage = () => {
  const [usage, setUsage] = useState(null);
  const [settings, setSettings] = useState(null);
  const [curatorUsers, setCuratorUsers] = useState([]);
  const [userTotal, setUserTotal] = useState(0);
  const [userPage, setUserPage] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  // Submitted separately from what is typed: this is a server query, so firing it per
  // keystroke would be a request per letter against the whole account table.
  const [userQuery, setUserQuery] = useState("");
  const [startDate, setStartDate] = useState(isoDaysAgo(DEFAULT_RANGE_DAYS));
  const [endDate, setEndDate] = useState(isoDaysAgo(0));
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  // Paging or searching the user table is its own request. Folding it into `load` meant
  // a page click also re-fetched the usage range and every setting — the spend chart
  // redrawing because someone looked at the second page of users.
  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const data = await settingsApi.getCuratorUsers({
        limit: USERS_PAGE_SIZE,
        offset: userPage * USERS_PAGE_SIZE,
        search: userQuery,
      });
      setCuratorUsers(data.users || []);
      setUserTotal(data.total || 0);
      setUsersError(null);
    } catch (err) {
      // Its own error state, not the page's: a failed user page must not replace the
      // spend view that loaded fine with a full-width red bar.
      setUsersError(err?.response?.data?.detail || "Could not load users");
    } finally {
      setUsersLoading(false);
    }
  }, [userPage, userQuery]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [usageData, settingsData] = await Promise.all([
        settingsApi.getCuratorUsage({ startDate, endDate }),
        settingsApi.getCuratorSettings(),
      ]);
      setUsage(usageData);
      setSettings(settingsData);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load curator data");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!message && !error) return undefined;
    const timer = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [message, error]);

  const save = async (patch) => {
    if (!settings) return;
    setSaving(true);
    const previous = settings;
    setSettings((current) => ({ ...current, ...patch }));
    try {
      const updated = await settingsApi.updateCuratorSettings(patch);
      setSettings(updated);
      setMessage("Saved");
      setError(null);
      // The spend tiles read the cap, so a budget change has to refresh them too.
      const usageData = await settingsApi.getCuratorUsage({ startDate, endDate });
      setUsage(usageData);
    } catch (err) {
      setSettings(previous);
      setError(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const summary = usage?.summary;
  const mode = settings?.mode || "OFF";
  // A settings row written before this axis existed has no `strategy` at all, and the API
  // fills it per key from the defaults — so FEED here matches what the backend would do
  // with the same row, rather than rendering a blank radio group.
  const strategy = settings?.strategy || "FEED";
  const budget = usage?.daily_budget_usd;
  const spentToday = usage?.spend_today;
  const budgetUsed = useMemo(() => {
    if (typeof spentToday !== "number" || !budget) return null;
    return Math.min(spentToday / budget, 1);
  }, [spentToday, budget]);

  const chartData = useMemo(
    () => (usage?.by_day || []).map((day) => ({ ...day, cost_usd: Number(day.cost_usd) || 0 })),
    [usage],
  );

  if (loading && !usage) {
    return (
      <div className="flex items-center justify-center p-12 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading curator data…
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Sparkles className="h-6 w-6" />
            Curator
          </h1>
          <p className="text-sm text-muted-foreground">
            The LLM recommendation mode: what it costs, and whether it is on.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            aria-label="Start date"
          />
          <span className="text-muted-foreground">→</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            aria-label="End date"
          />
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      <SettingsBanner message={message} error={error} />

      {!settings && !loading && (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Curator settings could not be loaded, so nothing here is editable. The usual cause
            is Elasticsearch still starting — this endpoint builds an admin service that needs
            it, even though the settings themselves live in Postgres. Check /health and hit
            Refresh.
          </CardContent>
        </Card>
      )}

      {/* Today against the cap is the only number worth watching in real time: past it,
          the mode goes dark until midnight UTC. */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Today
            <Badge variant={mode === "OFF" ? "secondary" : "default"}>
              {MODES.find((m) => m.value === mode)?.label || mode}
            </Badge>
            {/* "Everyone" alone no longer says what they get, and the two strategies cost
                an order of magnitude apart — so the badge that frames the spend below has
                to carry both. */}
            {mode !== "OFF" && (
              <Badge variant="secondary">
                {STRATEGIES.find((s) => s.value === strategy)?.label || strategy}
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            {spentToday === null || spentToday === undefined
              ? "The spend ledger could not be read — the mode refuses to spend while that is true."
              : `${money(spentToday)} of ${money(budget)} spent since midnight UTC.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {budgetUsed !== null && (
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className={`h-full ${budgetUsed >= 1 ? "bg-destructive" : "bg-primary"}`}
                style={{ width: `${budgetUsed * 100}%` }}
              />
            </div>
          )}
          {budgetUsed >= 1 && (
            <p className="mt-2 flex items-center gap-1 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4" />
              Budget reached — every request is being served by the ranker until midnight UTC.
            </p>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Spend in range" value={money(summary?.cost_usd, 4)} />
        <StatTile label="Curated requests" value={summary?.curated_requests ?? "—"} />
        <StatTile
          label="Avg per request"
          value={money(summary?.avg_cost_usd, 4)}
          hint="Over curated requests only — ranker requests made no call."
        />
        <StatTile
          label="Cache hit rate"
          value={percent(summary?.cache_hit_rate)}
          hint="A hit reuses stored picks and costs nothing."
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Cost by day</CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No curated requests in this range.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="date" fontSize={12} />
                <YAxis fontSize={12} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(value) => money(Number(value), 4)} />
                <Area type="monotone" dataKey="cost_usd" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Top spenders</CardTitle>
            <CardDescription>Anonymous devices show a device ID.</CardDescription>
          </CardHeader>
          <CardContent>
            {(usage?.by_user || []).length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Nothing yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead className="text-right">Requests</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usage.by_user.map((row) => (
                    <TableRow key={row.user_id || row.device_id}>
                      <TableCell className="font-mono text-xs">
                        {row.user_id || `device:${row.device_id}`}
                      </TableCell>
                      <TableCell className="text-right">{row.requests}</TableCell>
                      <TableCell className="text-right">{money(Number(row.cost_usd), 4)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Both strategies bill against one cap, so the tiles above blend them — and a
            call that buys 24 explanations and one that buys a single sentence have prices
            an order of magnitude apart. This is where that comparison is legible. */}
        <Card>
          <CardHeader>
            <CardTitle>Spend by policy</CardTitle>
            <CardDescription>
              What each policy actually cost, and what one call of it buys.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(usage?.by_mode || []).length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Nothing recorded in this range.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Policy</TableHead>
                    <TableHead className="text-right">Requests</TableHead>
                    <TableHead className="text-right">Avg per call</TableHead>
                    <TableHead className="text-right">Spend</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usage.by_mode.map((row) => (
                    <TableRow key={row.mode}>
                      <TableCell>{MODE_LABELS[row.mode] || row.mode}</TableCell>
                      <TableCell className="text-right">
                        {row.requests}
                        {row.cache_hits > 0 && (
                          <span className="ml-1 text-xs text-muted-foreground">
                            ({row.cache_hits} cached)
                          </span>
                        )}
                      </TableCell>
                      {/* Billed calls only, so this is the price of a call rather than
                          the price diluted by how well the cache happened to do. */}
                      {/* Raw, not `Number(...)`: `avg_cost_usd` is NULL for a policy with
                          no billed call — always for a day served entirely from cache —
                          and `Number(null)` is 0, which would print a claimed average
                          price of zero where `money` means to print an em dash. */}
                      <TableCell className="text-right">{money(row.avg_cost_usd, 5)}</TableCell>
                      <TableCell className="text-right font-medium">
                        {money(Number(row.cost_usd), 4)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Why requests were not curated</CardTitle>
            <CardDescription>
              &ldquo;paid&rdquo; means the call was billed and produced nothing usable.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(usage?.fallbacks || []).length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No fallbacks in this range.
              </p>
            ) : (
              <ul className="space-y-2">
                {usage.fallbacks.map((row) => (
                  <li key={row.reason} className="flex items-center justify-between text-sm">
                    <span>{FALLBACK_LABELS[row.reason] || row.reason}</span>
                    <span className="font-medium">{row.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {settings && (
      <Card>
        <CardHeader>
          <CardTitle>Settings</CardTitle>
          <CardDescription>
            Applied to the next request — no redeploy. A profile that has not completed
            onboarding is never curated, whatever is set here.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Three positions rather than a switch, because "off" and "everyone" are not
              opposites here — "selected" sits between them and is the one the list below
              serves. Radios rather than a dropdown: all three fit, and the consequence of
              each is the part worth reading. */}
          <fieldset disabled={saving}>
            <legend className="mb-2 text-sm font-medium">Who gets curator mode</legend>
            <div className="space-y-2">
              {MODES.map((option) => (
                <label key={option.value} className="flex cursor-pointer items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name="curator-mode"
                    className="mt-1"
                    value={option.value}
                    checked={mode === option.value}
                    onChange={() => save({ mode: option.value })}
                  />
                  <span>
                    <span className="font-medium">{option.label}</span>
                    <span className="block text-xs text-muted-foreground">{option.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          {/* Separate fieldset rather than a fourth position above: this answers a
              different question, and folding it in would make "selected users, on
              request" unspellable. */}
          <fieldset disabled={saving}>
            <legend className="mb-2 text-sm font-medium">What they get</legend>
            <div className="space-y-2">
              {STRATEGIES.map((option) => (
                <label key={option.value} className="flex cursor-pointer items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name="curator-strategy"
                    className="mt-1"
                    value={option.value}
                    checked={strategy === option.value}
                    onChange={() => save({ strategy: option.value })}
                  />
                  <span>
                    <span className="font-medium">{option.label}</span>
                    <span className="block text-xs text-muted-foreground">{option.hint}</span>
                  </span>
                </label>
              ))}
            </div>
            {mode === "OFF" && (
              <p className="mt-2 text-xs text-muted-foreground">
                Nobody is curated, so this changes nothing yet. Your choice is kept.
              </p>
            )}
          </fieldset>
          <div className="grid gap-4 sm:grid-cols-2">
            {NUMBER_SETTINGS.map((field) => (
              <NumberSetting
                key={field.key}
                field={field}
                value={settings?.[field.key]}
                disabled={saving}
                onCommit={(value) => save({ [field.key]: value })}
              />
            ))}
          </div>
        </CardContent>
      </Card>
      )}

      {settings && (
        <Card>
          <CardHeader>
            <CardTitle>Users</CardTitle>
            <CardDescription>
              Who is activated, and what each may spend. These decide anything only while
              the mode is <strong>Selected users</strong> — <strong>Off</strong> and{" "}
              <strong>Everyone</strong> ignore the list without editing it, so switching
              away and back restores exactly what is here.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <form
              className="flex flex-wrap items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                setUserPage(0);
                setUserQuery(userSearch.trim());
              }}
            >
              <input
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search by email or user ID"
                aria-label="Search users"
                className="w-64 rounded-md border border-border bg-background px-2 py-1 text-sm"
              />
              <Button type="submit" size="sm" variant="outline" disabled={saving}>
                Search
              </Button>
              {userQuery && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={saving}
                  onClick={() => {
                    setUserSearch("");
                    setUserPage(0);
                    setUserQuery("");
                  }}
                >
                  Clear
                </Button>
              )}
            </form>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Activated</TableHead>
                  <TableHead>Personal budget</TableHead>
                  <TableHead className="text-right">Spent today</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {curatorUsers.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className={`py-6 text-center text-sm ${usersError ? "text-destructive" : "text-muted-foreground"}`}
                    >
                      {usersError ||
                        (usersLoading
                          ? "Loading users…"
                          : userQuery
                            ? "No user matches that search."
                            : "No users yet.")}
                    </TableCell>
                  </TableRow>
                ) : (
                  curatorUsers.map((row) => (
                    <CuratorUserRow
                      key={row.user_id}
                      row={row}
                      disabled={saving}
                      onChange={(updated) =>
                        setCuratorUsers((rows) =>
                          rows.map((r) => (r.user_id === updated.user_id ? updated : r)),
                        )
                      }
                    />
                  ))
                )}
              </TableBody>
            </Table>
            {userTotal > USERS_PAGE_SIZE && (
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  {userPage * USERS_PAGE_SIZE + 1}–
                  {Math.min((userPage + 1) * USERS_PAGE_SIZE, userTotal)} of {userTotal}
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={usersLoading || userPage === 0}
                    onClick={() => setUserPage((p) => Math.max(0, p - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={usersLoading || (userPage + 1) * USERS_PAGE_SIZE >= userTotal}
                    onClick={() => setUserPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/**
 * An editable draft of `value` that follows `value` when it changes — set during render, the
 * way React's docs adjust state to a prop, rather than in an effect. An effect also runs on
 * mount, and when that mount effect is flushed late (a busy machine) its reset lands *after*
 * the admin's first keystroke and silently undoes it.
 */
const useSyncedDraft = (value) => {
  const [draft, setDraft] = useState(value ?? "");
  const [synced, setSynced] = useState(value);
  if (synced !== value) {
    setSynced(value);
    setDraft(value ?? "");
  }
  return [draft, setDraft];
};

/**
 * One row: activation, personal cap, and what they have spent today.
 *
 * Every account gets a row whether or not anything is stored for it — being un-activated
 * is the default, not something an admin creates — so the checkbox is the whole flow and
 * there is no add and no remove. Un-ticking keeps the personal budget, which is the point:
 * turning someone off for a week should not lose what they were allowed to spend.
 *
 * The budget commits on blur for the same reason the global one does — "0" is a
 * legitimate intermediate state of "0.25", and saving per keystroke would stop that user
 * dead before the rest of the number arrived.
 */
const CuratorUserRow = ({ row, onChange, disabled }) => {
  const [draft, setDraft] = useSyncedDraft(row.daily_budget_usd);
  const [busy, setBusy] = useState(false);

  const write = async (patch) => {
    setBusy(true);
    try {
      onChange(await settingsApi.setCuratorUser(row.user_id, patch));
    } finally {
      setBusy(false);
    }
  };

  const commitBudget = () => {
    const parsed = Number(draft);
    if (draft === "" && row.daily_budget_usd != null) return write({ clear_budget: true });
    if (draft === "" || Number.isNaN(parsed) || parsed === row.daily_budget_usd) {
      setDraft(row.daily_budget_usd ?? "");
      return undefined;
    }
    return write({ daily_budget_usd: parsed });
  };

  const overspent =
    typeof row.spend_today === "number" &&
    row.daily_budget_usd != null &&
    row.spend_today >= row.daily_budget_usd;

  return (
    <TableRow>
      <TableCell>
        <span className="block text-sm">{row.email || "(user deleted)"}</span>
        <span className="font-mono text-xs text-muted-foreground">{row.user_id}</span>
      </TableCell>
      <TableCell>
        <input
          type="checkbox"
          checked={row.enabled}
          disabled={disabled || busy}
          aria-label={`Curator mode for ${row.email || row.user_id}`}
          onChange={(e) => write({ enabled: e.target.checked })}
        />
      </TableCell>
      <TableCell>
        <input
          type="number"
          step={0.05}
          min={0}
          value={draft}
          disabled={disabled || busy}
          aria-label={`Personal budget for ${row.email || row.user_id}`}
          placeholder="global"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitBudget}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
          className="w-24 rounded-md border border-border bg-background px-2 py-1 text-sm"
        />
      </TableCell>
      <TableCell className={`text-right ${overspent ? "text-destructive" : ""}`}>
        {money(row.spend_today, 4)}
      </TableCell>
    </TableRow>
  );
};

const StatTile = ({ label, value, hint }) => (
  <Card>
    <CardContent className="p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{value}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </CardContent>
  </Card>
);

/**
 * A number input that only writes on blur or Enter.
 *
 * Saving per keystroke would PUT once per digit — and on the budget field, "5" is a
 * legitimate intermediate state of "50" that would take the mode dark for the rest of
 * the day before the second digit landed.
 */
const NumberSetting = ({ field, value, disabled, onCommit }) => {
  const [draft, setDraft] = useSyncedDraft(value);

  const commit = () => {
    const parsed = Number(draft);
    if (draft === "" || Number.isNaN(parsed) || parsed === value) {
      setDraft(value ?? "");
      return;
    }
    onCommit(parsed);
  };

  return (
    <label className="block text-sm">
      <span className="font-medium">{field.label}</span>
      <input
        type="number"
        // The visible label also carries the hint sentence, so the accessible name would
        // otherwise be the whole paragraph.
        aria-label={field.label}
        value={draft}
        step={field.step}
        min={field.min}
        max={field.max}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1"
      />
      <span className="mt-1 block text-xs text-muted-foreground">{field.hint}</span>
    </label>
  );
};

export default CuratorPage;
